import {put, call} from 'redux-saga/effects';
import Lookup from "../../entities/lookup";
import * as Actions from "../actions/lookupActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type LookupAction = {
    lookupId: string;
    lookup: Lookup;
    lookups: Lookup[];
};

export function* fetchOneLookup(action: LookupAction) {
	try {
		const lookup: Lookup = yield call(api.findOne, 'lookup', action.lookupId);

		// Now we can update the user interface
		yield put({type: Actions.LOOKUP_FETCH_ONE_SUCCESS, lookupId: action.lookupId, lookup});
	}
	catch(err) {
		yield put({type: Actions.LOOKUP_FETCH_ONE_FAILURE, lookupId: action.lookupId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {LookupAction} action
 */
export function* saveLookup(action: LookupAction) {
    // console.debug('⮂ LOOKUP_SAVE saga', action.lookup?.id);
    
	if (action.lookup === undefined || action.lookup === null) {
		console.error('saveLookup received a null');
		throw new Error('saveLookup received a null');
	}

	if (!(action.lookup instanceof Lookup)) {
		action.lookup = new Lookup(action.lookup);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'lookup', action.lookup);
		if (addResult.isErr()) {
			console.error(`⮂ saveLookup: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.LOOKUP_SAVE_FAILURE, lookup: action.lookup, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.LOOKUP_SAVE, lookup: action.lookup});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'lookup', action.lookup.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.LOOKUP_SAVE_FAILURE, lookup: action.lookup, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {LookupAction} action
 * @param {Lookup[]} action.lookups
 */
export function* saveManyLookups(action: LookupAction) {
    // console.debug('⮂ LOOKUP_SAVE_MANY saga', action.lookups.map(lookup => lookup.id).join(','));
    
    if (!Array.isArray(action.lookups)) {
        console.error('saveManyLookups expected an array, received', action.lookups);
        throw new Error('saveManyLookups expected an array');
    }

	try {
		const toSave: Lookup[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.lookups.length; i++) {
			const lookup = action.lookups[i];
			lookup.persisted = false;
			toSave.push(lookup);
		}

		const addResult = yield call(idb.add, 'lookup', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyLookups: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.LOOKUP_SAVE_MANY_FAILURE, lookups: action.lookups, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.LOOKUP_SAVE_MANY, lookups: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.LOOKUP_SAVE_MANY_FAILURE, lookups: action.lookups, status: 500, err});

		console.error(`Error saving ${action.lookups.length} lookups to database`, err);
	}
}

/**
 * @param {LookupAction} action
 */
export function* removeLookup(action: LookupAction) {
    const userId = getCurrentUserId();
	if (!userId || action.lookup === undefined || action.lookup === null || !(action.lookup instanceof Lookup)) {
			console.error('removeLookup expected type Lookup, received', action.lookup);
			throw new Error('removeLookup expected type Lookup');
		}

	try {
		// Mark as deleted
		action.lookup.status = 'D';
		action.lookup.addedOn = newHRTimestamp();
		action.lookup.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'lookup', action.lookup);
		if (addResult.isErr()) {
			console.error(`⮂ removeLookup: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.LOOKUP_REMOVE_FAILURE, lookup: action.lookup, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.LOOKUP_REMOVE, lookup: action.lookup});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.LOOKUP_REMOVE_FAILURE, lookup: action.lookup, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Lookup.
	*
	* @param {LookupAction} action
	*/
export function* restoreLookup(action: LookupAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreLookup expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.lookup === undefined || action.lookup === null) {
        console.error('restoreLookup expected a Lookup, received null');
        throw new Error('restoreLookup expected a Lookup, received null');
    }
    else if (!(action.lookup instanceof Lookup)) action.lookup = new Lookup(action.lookup); 
    
	try {
		// Mark as restored
		action.lookup.status = 'U';
		action.lookup.addedOn = newHRTimestamp();
		action.lookup.addedBy = userId;
		action.lookup.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'lookup', action.lookup);
		if (addResult.isErr()) {
			console.error(`⮂ restoreLookup: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.LOOKUP_RESTORE_FAILURE, lookup: action.lookup, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.LOOKUP_RESTORE, lookup: action.lookup});
	}
	catch(err) {
		yield put({type: Actions.LOOKUP_RESTORE_FAILURE, lookup: action.lookup, status: 500, err});

		console.error('Error restoring from database', err);
	}
}